import { MINUTES_PER_DAY, SLOT_DURATION_MINUTES, SLOT_STEP_MINUTES } from './workingHoursConstants';
import {
  daySlotsFit,
  freeBoundsForDay,
  getCarryInMinutesForDay,
  withCarryInReserve,
} from './utils/slotOverlapUtils';
import {
  WorkingHoursAction,
  WORKING_HOURS_SET_SITE,
  WORKING_HOURS_SET_APPLY_MODE,
  WORKING_HOURS_SET_START_DATE,
  WORKING_HOURS_TOGGLE_DAY,
  WORKING_HOURS_ADD_SLOT,
  WORKING_HOURS_ADD_SLOT_AT,
  WORKING_HOURS_UPDATE_SLOT,
  WORKING_HOURS_REMOVE_SLOT,
  WORKING_HOURS_SET_SYSTEM_GROUP,
  WORKING_HOURS_SET_SYSTEM_GROUP_TYPES,
  WORKING_HOURS_RESTORE_DAY,
  WORKING_HOURS_OPEN_EDITOR,
  WORKING_HOURS_CLOSE_EDITOR,
  WORKING_HOURS_UPDATE_EDITOR_DRAFT,
  WORKING_HOURS_SET_EDITOR_APPLY_DAYS,
  WORKING_HOURS_APPLY_EDITOR,
  WORKING_HOURS_SET_CONFIGURATION_FROM_API,
  WORKING_HOURS_SET_VIEWING_SYSTEM_GROUP_TYPE,
  WORKING_HOURS_SET_SITES,
  WORKING_HOURS_SET_SITE_CONFIGURATION_FROM_API,
} from 'actions/workingHoursActions';
import { WorkingHoursState, WorkingDay } from './WorkingHours.types';

export type { WorkingHoursAction };

// Empty until the real GET /api/production-hours/configuration response arrives.
const EMPTY_DAYS: WorkingDay[] = [
  { id: 'monday', name: 'Monday', enabled: false, slots: [] },
  { id: 'tuesday', name: 'Tuesday', enabled: false, slots: [] },
  { id: 'wednesday', name: 'Wednesday', enabled: false, slots: [] },
  { id: 'thursday', name: 'Thursday', enabled: false, slots: [] },
  { id: 'friday', name: 'Friday', enabled: false, slots: [] },
  { id: 'saturday', name: 'Saturday', enabled: false, slots: [] },
  { id: 'sunday', name: 'Sunday', enabled: false, slots: [] },
];

export const INITIAL_WORKING_HOURS_STATE: WorkingHoursState = {
  selectedSite: '',
  selectedSites: [],
  applyMode: 'immediately',
  startDate: '',
  selectedSystemGroup: '',
  selectedSystemGroupTypes: [],
  editor: null,
  configurationBySystemGroupType: {},
  isMixedSystemGroupTypes: false,
  viewingSystemGroupType: '',
  configurationBySite: {},
  isMixedSites: false,
  configurationLoadId: 0,
  days: EMPTY_DAYS,
};

export const workingHoursReducer = (
  state: WorkingHoursState = INITIAL_WORKING_HOURS_STATE,
  action: WorkingHoursAction
): WorkingHoursState => {
  switch (action.type) {
    case WORKING_HOURS_SET_SITE:
      return { ...state, selectedSite: action.payload };

    case WORKING_HOURS_SET_SITES:
      return { ...state, selectedSites: action.payload };

    case WORKING_HOURS_SET_APPLY_MODE:
      return { ...state, applyMode: action.payload };

    case WORKING_HOURS_SET_START_DATE:
      return { ...state, startDate: action.payload };

    case WORKING_HOURS_TOGGLE_DAY:
      return {
        ...state,
        days: state.days.map(day =>
          day.id === action.payload ? { ...day, enabled: !day.enabled } : day
        ),
      };

    case WORKING_HOURS_ADD_SLOT: {
      const { dayId, slotId } = action.payload;
      return {
        ...state,
        days: state.days.map(day => {
          if (day.id !== dayId) return day;
          const sorted = [...day.slots].sort((a, b) => a.startMinutes - b.startMinutes);
          let start = getCarryInMinutesForDay(state.days, dayId);
          for (const slot of sorted) {
            if (slot.startMinutes - start >= SLOT_DURATION_MINUTES) break;
            start = Math.max(start, slot.endMinutes);
          }
          if (start + SLOT_DURATION_MINUTES > MINUTES_PER_DAY) return day;
          const newSlot = {
            id: slotId,
            startMinutes: start,
            endMinutes: start + SLOT_DURATION_MINUTES,
          };
          return { ...day, slots: [...day.slots, newSlot] };
        }),
      };
    }

    case WORKING_HOURS_ADD_SLOT_AT: {
      const { dayId, slotId, startMinutes, durationMinutes } = action.payload;
      const reserve = getCarryInMinutesForDay(state.days, dayId);
      return {
        ...state,
        days: state.days.map(day => {
          if (day.id !== dayId) return day;
          const bounds = freeBoundsForDay(withCarryInReserve(day.slots, reserve), startMinutes);
          if (!bounds) return day; // clicked/dropped inside an existing slot or the carry-in reserve
          const [lo, hi] = bounds;
          const start = Math.max(startMinutes, lo);
          const duration = Math.max(SLOT_STEP_MINUTES, Math.min(durationMinutes, hi - start));
          if (duration < SLOT_STEP_MINUTES) return day;
          const newSlot = { id: slotId, startMinutes: start, endMinutes: start + duration };
          return { ...day, slots: [...day.slots, newSlot] };
        }),
      };
    }

    case WORKING_HOURS_REMOVE_SLOT: {
      const { dayId, slotId } = action.payload;
      return {
        ...state,
        days: state.days.map(day => {
          if (day.id !== dayId) return day;
          const remainingSlots = day.slots.filter(slot => slot.id !== slotId);
          return {
            ...day,
            slots: remainingSlots,
            enabled: remainingSlots.length > 0 ? day.enabled : false,
          };
        }),
      };
    }

    case WORKING_HOURS_UPDATE_SLOT: {
      const { dayId, slotId, startMinutes, endMinutes } = action.payload;
      return {
        ...state,
        days: state.days.map(day => {
          if (day.id !== dayId) return day;
          return {
            ...day,
            slots: day.slots.map(slot =>
              slot.id === slotId ? { ...slot, startMinutes, endMinutes } : slot
            ),
          };
        }),
      };
    }

    case WORKING_HOURS_SET_SYSTEM_GROUP:
      return { ...state, selectedSystemGroup: action.payload };

    case WORKING_HOURS_SET_SYSTEM_GROUP_TYPES:
      return { ...state, selectedSystemGroupTypes: action.payload };

    case WORKING_HOURS_RESTORE_DAY: {
      const { dayId, slots, enabled } = action.payload;
      return {
        ...state,
        days: state.days.map(day =>
          day.id === dayId ? { ...day, slots, enabled } : day
        ),
      };
    }

    case WORKING_HOURS_OPEN_EDITOR: {
      const { dayId, slotId } = action.payload;
      const day = state.days.find(d => d.id === dayId);
      const slot = day?.slots.find(s => s.id === slotId);
      if (!day || !slot) return state;
      return {
        ...state,
        editor: {
          dayId,
          slotId,
          startMinutes: slot.startMinutes,
          endMinutes: slot.endMinutes,
          overnight: slot.endMinutes <= slot.startMinutes,
          // Home day starts selected but is a normal, removable chip like any other day.
          applyDays: [dayId],
        },
      };
    }

    case WORKING_HOURS_CLOSE_EDITOR:
      return { ...state, editor: null };

    case WORKING_HOURS_UPDATE_EDITOR_DRAFT: {
      if (!state.editor) return state;
      return { ...state, editor: { ...state.editor, ...action.payload } };
    }

    case WORKING_HOURS_SET_EDITOR_APPLY_DAYS: {
      if (!state.editor) return state;
      return { ...state, editor: { ...state.editor, applyDays: action.payload.applyDays } };
    }

    case WORKING_HOURS_APPLY_EDITOR: {
      const { editor } = state;
      if (!editor) return state;
      const { dayId: homeDayId, slotId, startMinutes, overnight, applyDays } = editor;
      const finalEnd = overnight ? editor.endMinutes % MINUTES_PER_DAY : editor.endMinutes;
      if (!overnight && finalEnd <= startMinutes) return state;

      const candidate = { startMinutes, endMinutes: finalEnd };
      const targetDayIds = new Set(applyDays);

      const days = state.days.map(day => {
        const reserve = getCarryInMinutesForDay(state.days, day.id);

        if (day.id === homeDayId) {
          if (!targetDayIds.has(homeDayId)) {
            // Home day was deselected — the frame no longer lives here.
            const remainingSlots = day.slots.filter(s => s.id !== slotId);
            return {
              ...day,
              slots: remainingSlots,
              enabled: remainingSlots.length > 0 ? day.enabled : false,
            };
          }
          if (!daySlotsFit(withCarryInReserve(day.slots, reserve), slotId, candidate)) return day; // conflict — leave unchanged
          return {
            ...day,
            slots: day.slots.map(s =>
              s.id === slotId ? { ...s, startMinutes, endMinutes: finalEnd } : s
            ),
          };
        }

        if (!targetDayIds.has(day.id)) return day;
        // Deterministic id for this day's mirrored copy of the source slot — re-applying
        // the same source here (e.g. after nudging its time) must replace that copy, not
        // append a second slot sharing its id (updates/deletes match by id).
        const mirroredSlotId = `${day.id}-${slotId}`;
        const otherSlots = day.slots.filter(s => s.id !== mirroredSlotId);
        if (!daySlotsFit(withCarryInReserve(otherSlots, reserve), null, candidate)) return day; // conflict — skip this day
        const newSlot = { id: mirroredSlotId, startMinutes, endMinutes: finalEnd };
        return { ...day, enabled: true, slots: [...otherSlots, newSlot] };
      });

      return { ...state, days, editor: null };
    }

    case WORKING_HOURS_SET_CONFIGURATION_FROM_API: {
      const { bySystemGroupType, isMixed } = action.payload;
      const preferredType =
        state.viewingSystemGroupType && bySystemGroupType[state.viewingSystemGroupType]
          ? state.viewingSystemGroupType
          : state.selectedSystemGroupTypes.find(group => bySystemGroupType[group]) ?? '';
      const days = (preferredType && bySystemGroupType[preferredType]) || state.days;
      return {
        ...state,
        days,
        configurationBySystemGroupType: bySystemGroupType,
        isMixedSystemGroupTypes: isMixed,
        viewingSystemGroupType: preferredType,
        configurationLoadId: state.configurationLoadId + 1,
      };
    }

    case WORKING_HOURS_SET_VIEWING_SYSTEM_GROUP_TYPE: {
      const viewingSystemGroupType = action.payload;
      const days = state.configurationBySystemGroupType[viewingSystemGroupType] ?? state.days;
      return { ...state, viewingSystemGroupType, days };
    }

    case WORKING_HOURS_SET_SITE_CONFIGURATION_FROM_API: {
      const { bySite, isMixed } = action.payload;
      const days = bySite[state.selectedSite] ?? state.days;
      return {
        ...state,
        days,
        configurationBySite: bySite,
        isMixedSites: isMixed,
        configurationLoadId: state.configurationLoadId + 1,
      };
    }

    default:
      return state;
  }
};
